import {useCallback} from 'react';

import {
  ToolType,
  TOOL_DISPLAY_NAMES_IN_PROGRESS,
  isMCPTool,
  getBasicToolDisplayName,
} from 'llm/chat-library/tool-details/types';
import {useMCPToolMappingOptional} from 'llm/mcp-client-library/MCPToolMappingContext';
import {error as logError} from 'util/Logging';

/**
 * React hook for getting user-friendly tool display names.
 *
 * For MCP tools, returns friendly names like "Server Name: Tool Title"
 * instead of the raw "serverApiId__toolName" format.
 *
 * For built-in tools, returns the configured description from the unified
 * TOOL_DISPLAY_NAMES_IN_PROGRESS map.
 */
export function useToolDisplayName(): (toolName: ToolType | string) => string {
  const mcpMapping = useMCPToolMappingOptional();

  return useCallback(
    (toolName: ToolType | string): string => {
      if (isMCPTool(toolName)) {
        return mcpMapping?.getFullToolDisplayName(toolName) ?? getBasicToolDisplayName(toolName);
      }

      if (TOOL_DISPLAY_NAMES_IN_PROGRESS.hasOwnProperty(toolName)) {
        return TOOL_DISPLAY_NAMES_IN_PROGRESS[toolName as ToolType] ?? toolName;
      }

      logError(`[llm] Unknown tool name: ${toolName}`);
      return toolName;
    },
    [mcpMapping]
  );
}
